import * as React from 'react';
import {
  ADMIN_GROUPS,
  ALL_TABLES,
  AMAZON_EMPLOYEE,
  DZ_GLUE_DATASOURCE_ID,
  DZ_REDSHIFT_DATASOURCE_ID,
  EXP_12_HOURS_MINS,
  GLADSTONE_GROUP,
  GLADSTONE_WORKSPACE,
  GROUP_WKS_ID_DELIMITER,
  HC_AUTO_INHERITED_ASSOCIATION,
  HC_USER_EXPLICIT_ASSOCIATION,
  IAM_DATASOURCE_ID,
  LAKE_FORMATION_DATASOURCE_ID,
  LAKEFORMATION_RESOURCE,
  OMNI_ONCALL_URL,
  PERM_TYPE_DZ_GLUE,
  PERM_TYPE_DZ_REDSHIFT,
  PERM_TYPE_LF,
  PERM_TYPE_LF_FGAC,
  PERM_TYPE_LF_TAG,
  PERM_TYPE_REDSHIFT,
  REDSHIFT_DATASOURCE_ID,
  REDSHIFT_RESOURCE,
  TEMPLATE_TYPE_ACCESS_MANAGEMENT,
  TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW,
} from 'src/commons/constants';
import { isValidAccountId, isValidGalaxiGroup, isValidPrincipal, isValidWorkspace } from 'src/commons/validationUtils';
import {
  Box,
  CopyToClipboard,
  KeyValuePairs,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  TextContent,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import { getHCAdminGroup, getPartition, getRegion } from 'src/api/config';
import { getTemplateForResource, listFgaPolicies, listWorkspaces } from 'src/api/permissions';
import _ from 'lodash';
import { LocalStorageItem, LocalStorageItemKey, StorageService } from 'src/components/utils/storage-service';
import { listCatalogs } from 'src/api/catalog';
import { GlossaryDetails, HCMetadataAssociation } from 'aws-sdk/clients/awsdlhybridcatalogservicelambda';
import {
  generateArnFromId,
  generateIdFromIdInfo,
  getIdInfoFromArn,
  getScopeForArn,
  IdInfo,
} from 'src/components/utils/arnUtil';
import { createGroupDetailLink, createWorkspaceDetailLink } from 'src/routes';
import { GladstoneRequestItem, TemplatesList } from 'aws-sdk/clients/awsdatalakegladstonelambda';
import { i18nStrings } from 'src/commons/tables';
import { Avatar } from '@amzn/awsui-chat-components-console';

export interface DefaultRouteProps {
  catalogMap?: Map<string, string>;
  workspaceNameMap?: Map<string, string>;
  userInfo?: any;
  // Active group object
  activeGroupInfo?: any;
  // active workspace object
  activeWorkspace?: any;
}

export const GLOSSARY_RESOURCE_ASSN_CSV_KEYS = [
  'resourceArn',
  'governanceTag',
  'resourceType',
  'associationType',
  'catalogId',
  'databaseName',
  'tableName',
  'region',
  'dataSourceId',
  'ownerId',
  'fineGrainAccessPolicyId',
];

export const getGlossaryAssociationType = (glossaryAssociation: HCMetadataAssociation): string => {
  if (
    !glossaryAssociation ||
    !glossaryAssociation.AssociationAssignmentType ||
    'UserExplicit' === glossaryAssociation.AssociationAssignmentType
  )
    return HC_USER_EXPLICIT_ASSOCIATION;
  else return HC_AUTO_INHERITED_ASSOCIATION;
};

export const flattenGlossaryResourceAssociation = (item: HCMetadataAssociation, governanceTags: GlossaryDetails[]) => {
  if (!item) return {};
  if (!governanceTags) governanceTags = [];
  try {
    const resourceScopeType = getScopeForArn(item?.Resource);
    const idInfo: IdInfo = getIdInfoFromArn(item?.Resource);
    const associationType: string = getGlossaryAssociationType(item);
    return {
      resourceArn: item?.Resource,
      governanceTag: governanceTags.find((tag) => tag.GlossaryId === item.GlossaryId)?.GlossaryValue,
      resourceType: resourceScopeType,
      associationType: associationType,
      ownerId: item.OwnerId,
      region: idInfo.region,
      schemaName: idInfo.schemaName,
      clusterId: idInfo.clusterId,
      redshiftWorkgroupName: idInfo.redshiftWorkgroupName,
      databaseName: idInfo.databaseName,
      catalogId: idInfo.catalogId,
      dataSourceId: idInfo.dataSource,
      tableName: idInfo.tableName,
      fineGrainAccessPolicyId: idInfo.fineGrainAccessPolicyId,
    };
  } catch (e) {
    console.log('Exception flattening data glossary resource association ', e);
  }
  return {};
};

export const getCsvDataForGlossaryResources = (flattenedData: any) => {
  return getCsvDataFromFlattened(flattenedData, GLOSSARY_RESOURCE_ASSN_CSV_KEYS);
};

export const DATA_PERMISSION_CSV_KEYS = [
  'dataPermissionId',
  'permissionType',
  'catalogId',
  'databaseName',
  'tableName',
  'permissions',
  'dataLakePrincipal',
  'region',
  'type',
  'option',
  'dateActive',
  'activatedBy',
  'permissionRequestId',
  'permissionRequestedDate',
  'auditStatus',
  'dateOfLastAudit',
  'fgapId',
  'fgapName',
  'tag',
];

export const getCsvDataForDataPermission = (flattenedDataPermission: any) => {
  return getCsvDataFromFlattened(flattenedDataPermission, DATA_PERMISSION_CSV_KEYS);
};

export const getCsvDataFromFlattened = (flattenedDataRequest: any, keys: string[]) => {
  const csvData = {};
  if (!flattenedDataRequest) return csvData;

  keys.forEach((key) => {
    let value = flattenedDataRequest[key];
    let isComplex = (typeof value === 'object' || Array.isArray(value)) && value !== null;
    csvData[key] = isComplex ? JSON.stringify(value) : value ?? '';
  });
  return csvData;
};

export const getPermissionTypeForDPResource = (dpResource: any) => {
  if (!dpResource) return '';
  if (dpResource.tableWithColumns != undefined) {
    return PERM_TYPE_LF;
  } else if (dpResource.table != undefined) {
    return PERM_TYPE_LF;
  } else if (dpResource.dataCellsFilter != undefined) {
    return PERM_TYPE_LF_FGAC;
  } else if (dpResource.lFTagPolicy != undefined) {
    return PERM_TYPE_LF_TAG;
  } else if (dpResource.redshiftTagPolicy != undefined) {
    return PERM_TYPE_REDSHIFT;
  } else if (dpResource.dataZoneResource != undefined) {
    return !!dpResource.dataZoneResource.clusterId || !!dpResource.dataZoneResource.redshiftWorkgroupName
      ? PERM_TYPE_DZ_REDSHIFT
      : PERM_TYPE_DZ_GLUE;
  }
};

export const flattenDataPermission = (item: any) => {
  if (!item) return {};
  try {
    let tableName = undefined;
    let databaseName = undefined;
    let schemaName = undefined;
    let clusterId = undefined;
    let redshiftWorkgroupName = undefined;
    let bucketName = undefined;
    let catalogId = undefined;
    let fgapName = undefined;
    let columns = undefined;
    let tag = undefined;
    let dataSourceId = undefined;
    let permissionType = getPermissionTypeForDPResource(item.resource);
    if (item.resource?.table !== undefined) {
      databaseName = item.resource.table.databaseName;
      catalogId = item.resource.table.catalogId;
      bucketName = item.resource.table.bucketName;
      tableName = item.resource.table.name == null ? ALL_TABLES : item.resource.table.name;
      dataSourceId = LAKE_FORMATION_DATASOURCE_ID;
    } else if (item.resource?.tableWithColumns !== undefined) {
      databaseName = item.resource.tableWithColumns.databaseName;
      catalogId = item.resource.tableWithColumns.catalogId;
      bucketName = item.resource.tableWithColumns.bucketName;
      tableName = item.resource.tableWithColumns.name;
      columns = item.resource.tableWithColumns.columnNames;
      dataSourceId = LAKE_FORMATION_DATASOURCE_ID;
    } else if (item.resource?.lFTagPolicy !== undefined) {
      let policy = item.resource?.lFTagPolicy;
      let tagKey = policy.expression[0].tagKey;
      let tagValue = policy.expression[0].tagValues[0];
      catalogId = policy.catalogId;
      tag = `${tagKey}.${tagValue}`;
      dataSourceId = LAKE_FORMATION_DATASOURCE_ID;
    } else if (item.resource?.redshiftTagPolicy !== undefined) {
      let policy = item.resource.redshiftTagPolicy;
      tag = `${policy.tagKey}.${policy.tagValue}`;
      catalogId = policy.catalogId;
      dataSourceId = REDSHIFT_DATASOURCE_ID;
    } else if (item.resource?.dataCellsFilter) {
      catalogId = item.resource.dataCellsFilter.tableCatalogId;
      bucketName = item.resource.dataCellsFilter.bucketName;
      clusterId = item.resource.dataCellsFilter.clusterId;
      databaseName = item.resource.dataCellsFilter.databaseName;
      schemaName = item.resource.dataCellsFilter.schemaName;
      tableName = item.resource.dataCellsFilter.tableName;
      fgapName = item.resource.dataCellsFilter.name;
      dataSourceId = clusterId ? REDSHIFT_DATASOURCE_ID : LAKE_FORMATION_DATASOURCE_ID;
    } else if (item.resource?.glueTable) {
      databaseName = item.resource.glueTable.databaseName;
      catalogId = item.resource.glueTable.catalogId;
      tableName = item.resource.glueTable.name;
      dataSourceId = IAM_DATASOURCE_ID;
    } else if (item.resource?.glueDatabase) {
      databaseName = item.resource.glueDatabase.name;
      catalogId = item.resource.glueDatabase.catalogId;
      dataSourceId = IAM_DATASOURCE_ID;
    } else if (item.resource?.dataZoneResource) {
      databaseName = item.resource.dataZoneResource.databaseName;
      catalogId = item.resource.dataZoneResource.awsAccountId;
      tableName = item.resource.dataZoneResource.tableName;
      schemaName = item.resource.dataZoneResource.schemaName;
      clusterId = item.resource.dataZoneResource.clusterId;
      redshiftWorkgroupName = item.resource.dataZoneResource.redshiftWorkgroupName;
      dataSourceId = clusterId || redshiftWorkgroupName ? DZ_REDSHIFT_DATASOURCE_ID : DZ_GLUE_DATASOURCE_ID;
    }

    return {
      dataPermissionId: item.dataPermissionId,
      metadata: item.metadata,
      ownerId: item.ownerId,
      status: item.status,
      statusReason: item.statusReason,
      optimisticApprovalReason: item.optimisticApprovalReason,
      option: item.option,
      type: item.type,
      region: item.region,
      schemaName: schemaName,
      clusterId: clusterId,
      redshiftWorkgroupName: redshiftWorkgroupName,
      bucketName: bucketName,
      databaseName: databaseName,
      catalogId: catalogId,
      dataSourceId: dataSourceId,
      tableName: tableName,
      columns: columns,
      tag: tag,
      permissions: item.permissions,
      permissionsWithGrantOption: item.permissionsWithGrantOption,
      dataLakePrincipal: item.dataLakePrincipal,
      auditStatus: item.auditStatus,
      dateActive: item.dateActive,
      dateInActive: item.dateInActive,
      dateOfLastAudit: item.dateOfLastAudit,
      activatedBy: item.activatedBy,
      deactivatedBy: item.deactivatedBy,
      reasonOfAction: item.reasonOfAction,
      audit: item.audit,
      ticketDetails: item.ticketDetails,
      resource: tag != null ? tag : tableName,
      fgapId: item.fgapId,
      fgapName: fgapName,
      permissionRequestId: item.permissionRequestId,
      permissionRequestedDate: item.permissionRequestedDate,
      permissionType: permissionType,
      dataConsumerType: item.dataConsumerType,
    };
  } catch (e) {
    console.log('Exception flattening data permission ', e);
  }
  return {};
};

export const DATA_REQUEST_CSV_KEYS = [
  'requestId',
  'status',
  'statusReason',
  'timeOfRequest',
  'timeOfDecision',
  'decidedBy',
  'permissionType',
  'requestType',
  'catalogId',
  'databaseName',
  'tableName',
  'fgapName',
  'tag',
  'permissions',
  'dataLakePrincipal',
  'region',
  'permissions',
  'approvalIds',
  'simTicketLinks',
  'requestedBy',
  'requestedTo',
];

export const getCsvDataForDataRequest = (flattenedData: any) => {
  return getCsvDataFromFlattened(flattenedData, DATA_REQUEST_CSV_KEYS);
};

export const flattenRequestForCSV = (item: GladstoneRequestItem) => {
  if (!item) return {};
  try {
    let tableName = undefined;
    let databaseName = undefined;
    let schemaName = undefined;
    let clusterId = undefined;
    let catalogId = undefined;
    let fgapName = undefined;
    let columns = undefined;
    let tag = undefined;
    let region = undefined;
    let permissions = undefined;
    let datalakePrincipal = undefined;
    let permissionType = undefined;
    let approvalIds = [];
    let simTicketLinks = [];
    let requestedBy = item.ownerRequestedBy ? item.ownerRequestedBy : item.groupRequestedBy;
    let requestedTo = item.ownerRequestedTo ? item.ownerRequestedTo : item.groupRequestedTo;
    if (item.additionalMetadata && item.additionalMetadata.accessManagementInfoList) {
      item.additionalMetadata.accessManagementInfoList.forEach((ami) => {
        if (!_.isEmpty(ami.approvalWorkflowId)) approvalIds.push(ami.approvalWorkflowId);
        if (ami.simTicketLinks) {
          ami.simTicketLinks
            .filter((sim) => !_.isEmpty(sim))
            .forEach((sim) => {
              simTicketLinks.push(sim.simTicketLink);
            });
        }
      });
    }
    if (item.additionalMetadata && item.additionalMetadata.accessRequestWorkflowInfoList) {
      item.additionalMetadata.accessRequestWorkflowInfoList.forEach((arw) => {
        if (!_.isEmpty(arw.approvalWorkflowId)) approvalIds.push(arw.approvalWorkflowId);
      });
    }
    if (item.dataPermissionRequest !== undefined) {
      let dpResource = item.dataPermissionRequest.resource;
      region = item.dataPermissionRequest.dataLocationRegion;
      permissions = item.dataPermissionRequest.permissions;
      datalakePrincipal = item.dataPermissionRequest.dataLakePrincipal;
      permissionType = getPermissionTypeForDPResource(dpResource);
      if (dpResource.table !== undefined) {
        databaseName = dpResource.table.databaseName;
        catalogId = dpResource.table.catalogId;
        tableName = dpResource.table.name == null ? ALL_TABLES : dpResource.table.name;
      } else if (dpResource.tableWithColumns !== undefined) {
        databaseName = dpResource.tableWithColumns.databaseName;
        catalogId = dpResource.tableWithColumns.catalogId;
        tableName = dpResource.tableWithColumns.name;
        columns = dpResource.tableWithColumns.columnNames;
      } else if (dpResource.lFTagPolicy !== undefined) {
        let policy = dpResource?.lFTagPolicy;
        let tagKey = policy.expression[0].tagKey;
        let tagValue = policy.expression[0].tagValues[0];
        catalogId = policy.catalogId;
        tag = `${tagKey}.${tagValue}`;
      } else if (dpResource.redshiftTagPolicy !== undefined) {
        let policy = dpResource.redshiftTagPolicy;
        tag = `${policy.tagKey}.${policy.tagValue}`;
        catalogId = policy.catalogId;
      } else if (dpResource.dataCellsFilter) {
        catalogId = dpResource.dataCellsFilter.tableCatalogId;
        clusterId = dpResource.dataCellsFilter.clusterId;
        databaseName = dpResource.dataCellsFilter.databaseName;
        schemaName = dpResource.dataCellsFilter.schemaName;
        tableName = dpResource.dataCellsFilter.tableName;
        fgapName = dpResource.dataCellsFilter.name;
      } else if (dpResource.glueTable) {
        databaseName = dpResource.glueTable.databaseName;
        catalogId = dpResource.glueTable.catalogId;
        tableName = dpResource.glueTable.name;
      } else if (dpResource.glueDatabase) {
        databaseName = dpResource.glueDatabase.name;
        catalogId = dpResource.glueDatabase.catalogId;
      } else if (dpResource.dataZoneResource) {
        databaseName = dpResource.dataZoneResource.databaseName;
        catalogId = dpResource.dataZoneResource.awsAccountId;
        tableName = dpResource.dataZoneResource.tableName;
        schemaName = dpResource.dataZoneResource.schemaName;
        clusterId = dpResource.dataZoneResource.clusterId;
      }
    }
    if (item.lakeFormationRequest !== undefined) {
      datalakePrincipal = item.dataLakePrincipal;
      region = item.lakeFormationRequest.DataLocationRegion;
      permissions = item.lakeFormationRequest.PermissionsString
        ? JSON.parse(item.lakeFormationRequest.PermissionsString)
        : null;
      let resourceObj = item.lakeFormationRequest.ResourceString
        ? JSON.parse(item.lakeFormationRequest.ResourceString)
        : null;
      if (resourceObj.table) {
        databaseName = resourceObj.table.databaseName;
        catalogId = resourceObj.table.catalogId;
        tableName = resourceObj.table.name == null ? ALL_TABLES : resourceObj.table.name;
      } else if (resourceObj.tableWithColumns) {
        databaseName = resourceObj.tableWithColumns.databaseName;
        catalogId = resourceObj.tableWithColumns.catalogId;
        tableName = resourceObj.tableWithColumns.name;
        columns = resourceObj.tableWithColumns.columnNames;
      }
    }

    return {
      requestId: item.requestId,
      status: item.status,
      statusReason: item.statusReason,
      timeOfRequest: item.timeOfRequest,
      timeOfDecision: item.timeOfDecision,
      permissionType: permissionType,
      requestType: item.requestType,
      region: region,
      permissions: permissions,
      requestedBy: requestedBy,
      requestedTo: requestedTo,
      schemaName: schemaName,
      clusterId: clusterId,
      databaseName: databaseName,
      catalogId: catalogId,
      tableName: tableName,
      columns: columns,
      tag: tag,
      dataLakePrincipal: datalakePrincipal,
      decidedBy: item.decidedBy,
      fgapName: fgapName,
      approvedPermissionId: item.approvedPermissionId,
      approvedPermissionDate: item.approvedPermissionDate,
      approvalIds: approvalIds,
      simTicketLinks: simTicketLinks,
    };
  } catch (e) {
    console.log('Exception formatting request to flattenCSV', e);
  }
  return {};
};

export const generateCsvFileNameForConsumers = () => {
  // eg: consumers-2024-09-13.csv
  return `consumers-${new Date().toISOString().split('T')[0]}`;
};

export const generateCsvFileName = (prefix: string, ownerId: string) => {
  // eg: permissions-2024-09-13-AWSDL_Services.csv
  return `${prefix}-${new Date().toISOString().split('T')[0]}${ownerId ? '-' + ownerId : ''}`;
};

export const keyForCatalogNameMap = (idInfo: IdInfo) => {
  // if dataSource not provided, we can do our best to guess it
  let dataSourceOrDefault =
    idInfo.dataSource ?? (idInfo.redshiftWorkgroupName ? 'redshiftDZ' : idInfo.clusterId ? 'redshift' : 'glueLF');
  if (idInfo.redshiftWorkgroupName) {
    return (
      dataSourceOrDefault + ':' + idInfo.catalogId + ':serverless:' + idInfo.redshiftWorkgroupName + ':' + idInfo.region
    );
  } else if (idInfo.clusterId) {
    return dataSourceOrDefault + ':' + idInfo.catalogId + ':' + idInfo.clusterId + ':' + idInfo.region;
  } else if (idInfo.bucketName) {
    return dataSourceOrDefault + ':' + idInfo.catalogId + ':' + idInfo.bucketName + ':' + idInfo.region;
  } else {
    return dataSourceOrDefault + ':' + idInfo.catalogId + ':' + idInfo.region;
  }
};

export const generateCatalogMap = async (storageInstance: StorageService): Promise<Map<string, string>> => {
  const catalogMapItem = await storageInstance?.getItem(LocalStorageItemKey.CatalogNameMap);
  if (catalogMapItem) {
    return catalogMapItem.data as Map<string, string>;
  } else {
    let catalogNameMap = new Map();
    let request = { NextToken: null };
    let result = await listCatalogs(request);
    let catalogInfoList = result.CatalogInfoList;
    while (result.NextToken != null) {
      request.NextToken = result.NextToken;
      result = await listCatalogs(request);
      catalogInfoList.push(...result.CatalogInfoList);
    }
    for (let i = 0; i < catalogInfoList.length; i++) {
      let item = catalogInfoList[i];
      const catalogId: string = item.CatalogId;
      const region: string = item.Region;
      const clusterIdentifier: string = item.ClusterIdentifier;
      const redshiftWorkgroupName: string = item.RedshiftWorkgroupName;
      const bucketName: string = item.BucketName;
      const dataSource: string = item.DataSourceId;

      const key = keyForCatalogNameMap({
        dataSource,
        catalogId,
        clusterId: clusterIdentifier,
        redshiftWorkgroupName,
        bucketName,
        region,
      });
      catalogNameMap.set(key, item.Name);
    }
    await storageInstance?.setItem(
      LocalStorageItemKey.CatalogNameMap,
      new LocalStorageItem(catalogNameMap, EXP_12_HOURS_MINS),
    );
    return catalogNameMap;
  }
};

export const generateWorkspaceNameMap = async (storageInstance: StorageService): Promise<Map<string, string>> => {
  const wksNameMapItem = await storageInstance?.getItem(LocalStorageItemKey.WorkspaceNameMap);
  if (wksNameMapItem) {
    return wksNameMapItem.data as Map<string, string>;
  } else {
    let wksNameMap = new Map();
    let request = { workspaceIds: [], includeAllDetails: false, nextToken: null };
    let result = await listWorkspaces(request);
    let allWorkspaces = result.workspaces;
    while (result.nextToken != null) {
      request.nextToken = result.nextToken;
      result = await listWorkspaces(request);
      allWorkspaces.push(...result.workspaces);
    }
    allWorkspaces
      .filter((wks) => !!wks.workspaceName)
      .forEach((wks) => wksNameMap.set(wks.workspaceId, wks.workspaceName));
    await storageInstance?.setItem(
      LocalStorageItemKey.WorkspaceNameMap,
      new LocalStorageItem(wksNameMap, EXP_12_HOURS_MINS),
    );
    return wksNameMap;
  }
};

export const getGroupOrWorkspaceName = (ownerId: string, wksNameMap: Map<string, string>): string => {
  if (isValidWorkspace(ownerId) && wksNameMap) {
    return wksNameMap.has(ownerId) ? getWorkspaceDisplayName(wksNameMap.get(ownerId), ownerId) : ownerId;
  } else if (isValidGalaxiGroup(ownerId)) {
    return ownerId.replace(/^glx-/, '');
  } else {
    return ownerId;
  }
};

export const getWorkspaceDisplayName = (wksName: string, wksId: string): string => {
  // eg: akaanjWks, wks-061754192931-dc64db2 => akaanjWks (061754192931)
  return `${wksName} (${wksId.substring(4, 16)})`;
};

export const getDropdownItemId = (groupId: string, workspaceId: string): string => {
  // akaanjGroup, wks-061754192931-dc64db2 => akaanjGroup##wks-061754192931-dc64db2
  return `${groupId}${GROUP_WKS_ID_DELIMITER}${workspaceId}`;
};

export const getGroupAndWksFromDropdownItemId = (dropdownItemId: string): string[] => {
  // akaanjGroup##wks-061754192931-dc64db2 => akaanjGroup, wks-061754192931-dc64db2
  const grpAndWks = dropdownItemId?.split(GROUP_WKS_ID_DELIMITER) ?? '';
  return [grpAndWks[0] ?? '', grpAndWks[1] ?? ''];
};

export const getGroupFromDropdownItemId = (dropdownItemId: string): string => {
  return getGroupAndWksFromDropdownItemId(dropdownItemId)[0];
};

export const getWksFromDropdownItemId = (dropdownItemId: string): string => {
  return getGroupAndWksFromDropdownItemId(dropdownItemId)[1];
};

export const getOwnerIdName = async (ownerId: string): Promise<string> => {
  const wksNameMapItem = await StorageService.getInstance().getItem(LocalStorageItemKey.WorkspaceNameMap);
  let wksNameMap = wksNameMapItem?.data as Map<string, string>;
  if (isValidWorkspace(ownerId) && wksNameMap) {
    return wksNameMap.has(ownerId) ? `${wksNameMap.get(ownerId)} (Wks)` : ownerId;
  } else {
    return ownerId;
  }
};

export const getGroupingMechanismType = (groupingId: string) => {
  if (groupingId.includes('.')) {
    return 'Teams';
  } else {
    return 'Posix';
  }
};

export const getPhoneToolLink = (userId: string, withAvatar: boolean = false) => {
  const url = 'https://phonetool.amazon.com/users/';
  let userUrl = url.concat(userId);
  return withAvatar ? (
    <Link href={userUrl} rel='noopener noreferrer'>
      <SpaceBetween direction={'vertical'} size={'xxs'} alignItems={'center'}>
        <Avatar imgUrl={getPhoneToolUrl(userId)} ariaLabel={userId} />
        <>{userId}</>
      </SpaceBetween>
    </Link>
  ) : (
    <Link href={userUrl} external={true} rel='noopener noreferrer'>
      {userId}
    </Link>
  );
};

export const getPhoneToolUrl = (userId: string) => {
  const url = 'https://internal-cdn.amazon.com/badgephotos.amazon.com/?fullsizeimage=1&uid=';
  return url.concat(userId);
};

export const getRequestSubmittedWithSLAInDaysMessage = (num) => {
  return `The request was submitted successfully. Omni oncall has been notified and will process it within ${num} business day(s).`;
};

export const getRequestSubmittedWithAutoApproval = () => {
  return (
    <>
      The request was submitted successfully. In most cases, the request will be auto-approved in few minutes. <br />
      Please reach out to {getOmniOncallLink()} if not approved within an hour!
    </>
  );
};

export const getFoundryRoleARN = (_region, catalogId) => {
  return `arn:${getPartition()}:iam::${catalogId}:role/AWSDGSFoundry-ManagementRole-DO-NOT-DELETE`;
};

export const getOmniOncallLink = () => {
  return (
    <Link href={OMNI_ONCALL_URL} external={true}>
      Omni oncall
    </Link>
  );
};

export const groupingMechanismIdIsTeam = (groupingMechanismId: string) => {
  return groupingMechanismId?.includes('amzn1.');
};

export const groupingMechanismIdIsPosix = (groupingMechanismId: string) => {
  return groupingMechanismId && /^[-_a-z]+$/.test(groupingMechanismId);
};

export const groupingMechanismIdUrl = (groupingMechanismId: string) => {
  let isTeam: boolean = groupingMechanismIdIsTeam(groupingMechanismId);
  let isPosix: boolean = groupingMechanismIdIsPosix(groupingMechanismId);

  return isTeam
    ? `https://permissions.amazon.com/a/team/${groupingMechanismId}`
    : isPosix
    ? `https://permissions.amazon.com/group.mhtml?group=${groupingMechanismId}&group_type=posix`
    : groupingMechanismId;
};

export const getGroupMechanismIdLink = (groupingMechanismId: string, includeType: boolean = false) => {
  let isTeam: boolean = groupingMechanismIdIsTeam(groupingMechanismId);

  let groupingMechUrl = groupingMechanismIdUrl(groupingMechanismId);
  return (
    <TextContent>
      {includeType && <b>{isTeam ? 'Team ' : 'POSIX group '}</b>}
      <Link href={groupingMechUrl} external={true}>
        {groupingMechanismId}
      </Link>
    </TextContent>
  );
};

export const getCtiUrl = (c, t, i) => {
  return (
    'https://t.corp.amazon.com/create/options?ticket_type=Regular+Ticket&severity=FIVE&category=' +
    c +
    '&type=' +
    t +
    '&item=' +
    i
  );
};

export const getCtiUrlFromCti = (cti) => {
  if (!cti) return;
  const arr = cti.split('/');
  const c = arr[0];
  const t = arr[1];
  const i = arr[2];
  return getCtiUrl(c, t, i);
};

export const getAccountIdFromPrincipal = (principal) => {
  if (isValidPrincipal(principal)) {
    return principal.split(':')[4];
  }
  if (isValidAccountId(principal)) {
    return principal;
  }
  return '';
};

export const convertToDgsHCResourceArn = (
  accountId,
  clusterId,
  redshiftWorkgroupName,
  bucketName,
  databaseName,
  schemaName,
  tableName,
  columnName,
  dataSource,
) => {
  const idInfo: IdInfo = {
    catalogId: accountId,
    clusterId,
    redshiftWorkgroupName,
    bucketName,
    databaseName,
    schemaName,
    tableName,
    columnName,
    dataSource,
    region: getRegion(),
  };
  return generateArnFromId(generateIdFromIdInfo(idInfo));
};

export const convertToDgsHCResourceId = (
  accountId,
  clusterId,
  redshiftWorkgroupName,
  bucketName,
  databaseName,
  schemaName,
  tableName,
  columnName,
  dataSource,
) => {
  const idInfo: IdInfo = {
    catalogId: accountId,
    clusterId,
    redshiftWorkgroupName,
    bucketName,
    databaseName,
    schemaName,
    tableName,
    columnName,
    dataSource,
    region: getRegion(),
  };
  return generateIdFromIdInfo(idInfo);
};

export const isDataLakeAdmin = (group) => {
  return ADMIN_GROUPS.includes(group);
};

export const isDGSAdmin = (groups) => {
  if (!groups) return false;
  const dgsAdminGroupIds = ['AWSDL_Services', 'AWS_DGS'];
  return groups.filter((i) => dgsAdminGroupIds.includes(i)).length;
};

export const isHCAdmin = (group) => {
  return group == getHCAdminGroup();
};

export const registerStatus = (registered: boolean) => {
  return registered ? (
    <StatusIndicator>Registered</StatusIndicator>
  ) : (
    <StatusIndicator type='info'>Not Registered</StatusIndicator>
  );
};

export interface CopiableTextProps {
  name?: string;
  value?: any;
  loading?: boolean;
  url?: string;
  copiable?: boolean;
  external?: boolean;
}

export const CopiableText = ({
  name = '',
  value = '',
  loading = false,
  url = null,
  copiable = true,
  external = false,
}: CopiableTextProps) => {
  return loading ? (
    <Spinner />
  ) : (
    <KeyValuePairs
      items={[
        {
          label: name,
          value: copiable ? (
            <SpaceBetween size={'xxs'} direction={'horizontal'} alignItems={'center'}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  zIndex: 1,
                  justifyContent: 'space-between',
                  wordBreak: 'break-word',
                }}
              >
                <CopyToClipboard
                  textToCopy={value ?? ''}
                  copyButtonAriaLabel={'Copy to clipboard'}
                  copySuccessText={'Copied to clipboard'}
                  copyErrorText={'Failed to copy'}
                  variant={'icon'}
                />
                {url ? (
                  <Link href={url} external={external}>
                    {value}
                  </Link>
                ) : (
                  <Box>{value}</Box>
                )}
              </div>
            </SpaceBetween>
          ) : url ? (
            <Link href={url} external={external}>
              {value}
            </Link>
          ) : (
            value
          ),
        },
      ]}
    />
  );
};

export const fetchTemplatesForResourceId = async (resourceId: string): Promise<TemplatesList> => {
  let templateList = [];
  if (resourceId) {
    try {
      const getAMITemplatesForResourceResponse = await getTemplateForResource({
        resourceId: resourceId,
        templateType: TEMPLATE_TYPE_ACCESS_MANAGEMENT,
      });
      const getARWTemplatesForResourceResponse = await getTemplateForResource({
        resourceId: resourceId,
        templateType: TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW,
      });
      templateList.push(
        ...getAMITemplatesForResourceResponse.templates,
        ...getARWTemplatesForResourceResponse.templates,
      );
      return _.uniqBy(templateList, 'templateId');
    } catch (err) {
      console.log('Failed to fetch templates' + err.message);
    }
  }
  return [];
};

export const getConsumerDataLakePrincipal = (dataAssetType, consumerPrincipal, consumerAccount) => {
  if (dataAssetType == REDSHIFT_RESOURCE) {
    return consumerAccount;
  } else if (dataAssetType == LAKEFORMATION_RESOURCE) {
    return consumerPrincipal ?? consumerAccount;
  }
};

export const fetchFineGrainedAccessPolicies = async (ownerId, status, type, tableData) => {
  if (!ownerId && !tableData) return [];

  const fgaps = [];
  const request = {
    ownerId: ownerId,
    status: status,
    templateType: type,
    tableData: {
      tableCatalogId: tableData.CatalogId,
      clusterId: tableData.ClusterIdentifier,
      databaseName: tableData.DatabaseName,
      schemaName: tableData.SchemaName,
      tableName: tableData.TableName,
      region: tableData.Region,
    },
    nextToken: null,
  };

  try {
    do {
      const result = await listFgaPolicies(request);
      request.nextToken = result.nextToken;
      fgaps.push(...result.FineGrainAccess);
    } while (request.nextToken);
    return fgaps;
  } catch (err) {
    console.error('Failed to fetch fine grained access policy: ' + err.message);
    return [];
  }
};

export const generateLinkForReviewer = (reviewedBy: string, reviewerType) => {
  if (reviewerType == AMAZON_EMPLOYEE) {
    return `https://phonetool.amazon.com/users/${reviewedBy}`;
  }

  if (reviewerType == GLADSTONE_WORKSPACE) {
    return createWorkspaceDetailLink(reviewedBy);
  }

  if (reviewerType == GLADSTONE_GROUP) {
    return createGroupDetailLink(reviewedBy);
  }
  return null;
};

export const isSearchEnabled = () => {
  let region = getRegion();
  switch (region) {
    case 'us-east-1':
    case 'us-west-2':
    case 'cn-northwest-1':
      return true;
    default:
      return false;
  }
};

export const getHCResourcePagePropertyFilter = () => {
  return isSearchEnabled()
    ? {
        ...i18nStrings,
        filteringPlaceholder: 'To discover resources, use search page instead.',
      }
    : i18nStrings;
};

export const toLocalTime = (utcTimeString: string) => {
  return new Date(utcTimeString).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
};
